/**
 * Wishlist Button — stylerotate.co.nz
 *
 * Reads window.__WISHLIST_CONFIG set by wishlist-config.liquid.
 * Handles both guest (localStorage) and logged-in (API) modes.
 *
 * Usage in product template:
 *   <button class="wishlist-btn"
 *     data-product-id="{{ product.id }}"
 *     data-variant-id="{{ product.selected_or_first_available_variant.id }}"
 *     data-product-handle="{{ product.handle }}"
 *     data-product-title="{{ product.title | escape }}"
 *     data-image-url="{{ product.featured_image | img_url: '400x' }}"
 *     data-price="{{ product.selected_or_first_available_variant.price | money_without_currency }}"
 *     data-currency="{{ cart.currency.iso_code }}">
 *     ♡ Save
 *   </button>
 */
(function () {
  'use strict';

  var GUEST_KEY = 'wishlist_guest';
  var SESSION_TOKEN_KEY = 'wishlist_token';

  var cfg = window.__WISHLIST_CONFIG;
  if (!cfg) return;

  // ─── Storage helpers ────────────────────────────────────────────────────────

  function getGuestItems() {
    try {
      return JSON.parse(localStorage.getItem(GUEST_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function saveGuestItems(items) {
    localStorage.setItem(GUEST_KEY, JSON.stringify(items));
  }

  function getSessionToken() {
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
  }

  function setSessionToken(token) {
    sessionStorage.setItem(SESSION_TOKEN_KEY, token);
  }

  // ─── API helpers ────────────────────────────────────────────────────────────

  function apiRequest(method, path, body) {
    var token = getSessionToken();
    var headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = 'Bearer ' + token;

    return fetch(cfg.apiBase + path, {
      method: method,
      headers: headers,
      body: body ? JSON.stringify(body) : undefined,
    }).then(function (r) {
      if (r.status === 204) return null;
      return r.json();
    });
  }

  // ─── Auth ───────────────────────────────────────────────────────────────────

  function loginWithHmac(customer) {
    return apiRequest('POST', '/auth/login', {
      store_domain: cfg.storeDomain,
      customer_id: customer.id,
      hmac: cfg.hmac,
    }).then(function (data) {
      if (data && data.token) {
        setSessionToken(data.token);
        return data.token;
      }
      throw new Error('Login failed');
    });
  }

  // ─── Wishlist state ─────────────────────────────────────────────────────────

  var wishlisted = new Set(); // variant IDs currently wishlisted

  function loadWishlistedIds() {
    if (getSessionToken()) {
      return apiRequest('GET', '/wishlist?store_domain=' + encodeURIComponent(cfg.storeDomain))
        .then(function (data) {
          wishlisted.clear();
          if (data && data.items) {
            data.items.forEach(function (item) { wishlisted.add(item.variant_id); });
          }
          refreshAllButtons();
        })
        .catch(function () { /* silently degrade */ });
    } else {
      var items = getGuestItems();
      wishlisted.clear();
      items.forEach(function (item) { wishlisted.add(item.variant_id); });
      refreshAllButtons();
      return Promise.resolve();
    }
  }

  function refreshAllButtons() {
    document.querySelectorAll('.wishlist-btn').forEach(updateButton);
  }

  function updateButton(btn) {
    var variantId = btn.dataset.variantId;
    if (!variantId) return;
    var active = wishlisted.has('gid://shopify/ProductVariant/' + variantId);
    btn.setAttribute('aria-pressed', String(active));
    btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    btn.querySelector('.wishlist-icon') && (btn.querySelector('.wishlist-icon').textContent = active ? '♥' : '♡');
    btn.classList.toggle('wishlist-btn--active', active);
  }

  // ─── Toggle ─────────────────────────────────────────────────────────────────

  function buildItemPayload(btn) {
    return {
      store_domain: cfg.storeDomain,
      product_id: 'gid://shopify/Product/' + btn.dataset.productId,
      variant_id: 'gid://shopify/ProductVariant/' + btn.dataset.variantId,
      product_handle: btn.dataset.productHandle || '',
      product_title: btn.dataset.productTitle || '',
      image_url: btn.dataset.imageUrl || undefined,
      price: btn.dataset.price || undefined,
      currency: btn.dataset.currency || 'NZD',
    };
  }

  function toggleLoggedIn(btn, variantGid) {
    var removing = wishlisted.has(variantGid);

    if (removing) {
      // Optimistic remove
      wishlisted.delete(variantGid);
      updateButton(btn);

      return apiRequest('GET', '/wishlist?store_domain=' + encodeURIComponent(cfg.storeDomain))
        .then(function (data) {
          var item = data && data.items.find(function (i) { return i.variant_id === variantGid; });
          if (!item) return null;
          return apiRequest('DELETE', '/wishlist/items/' + item.id);
        })
        .catch(function () {
          // Revert on failure
          wishlisted.add(variantGid);
          updateButton(btn);
        });
    } else {
      // Optimistic add
      wishlisted.add(variantGid);
      updateButton(btn);

      return apiRequest('POST', '/wishlist/items', buildItemPayload(btn))
        .catch(function () {
          wishlisted.delete(variantGid);
          updateButton(btn);
        });
    }
  }

  function toggleGuest(btn, variantGid) {
    var items = getGuestItems();
    var idx = items.findIndex(function (i) { return i.variant_id === variantGid; });

    if (idx > -1) {
      items.splice(idx, 1);
      wishlisted.delete(variantGid);
    } else {
      var payload = buildItemPayload(btn);
      payload.added_at = new Date().toISOString();
      items.push(payload);
      wishlisted.add(variantGid);
    }

    saveGuestItems(items);
    updateButton(btn);
  }

  function onButtonClick(e) {
    e.stopPropagation();
    var btn = e.currentTarget;
    if (btn.dataset.variantId === undefined) return;

    var variantGid = 'gid://shopify/ProductVariant/' + btn.dataset.variantId;

    if (getSessionToken()) {
      toggleLoggedIn(btn, variantGid);
    } else {
      toggleGuest(btn, variantGid);
    }
  }

  // ─── Wishlist page rendering ─────────────────────────────────────────────────

  function renderWishlistPage(container) {
    container.innerHTML = '<p>Loading wishlist...</p>';

    function getItems() {
      if (getSessionToken()) {
        return apiRequest('GET', '/wishlist?store_domain=' + encodeURIComponent(cfg.storeDomain))
          .then(function (data) { return (data && data.items) || []; });
      } else {
        return Promise.resolve(getGuestItems());
      }
    }

    getItems().then(function (items) {
      if (!items.length) {
        container.innerHTML = '<p class="wishlist-empty">Your wishlist is empty.</p>';
        return;
      }

      var html = '<ul class="wishlist-items">';
      items.forEach(function (item) {
        var handle = item.product_handle || '';
        var price = item.price ? item.currency + ' ' + item.price : '';
        html += '<li class="wishlist-item" data-item-id="' + (item.id || '') + '" data-variant-id="' + item.variant_id + '">';
        if (item.image_url) {
          html += '<a href="/products/' + handle + '"><img src="' + item.image_url + '" alt="' + escapeHtml(item.product_title) + '" width="120" height="120" loading="lazy"></a>';
        }
        html += '<div class="wishlist-item__info">';
        html += '<a href="/products/' + handle + '" class="wishlist-item__title">' + escapeHtml(item.product_title) + '</a>';
        if (price) html += '<p class="wishlist-item__price">' + escapeHtml(price) + '</p>';
        html += '<button class="wishlist-remove-btn" data-item-id="' + (item.id || '') + '" data-variant-id="' + item.variant_id + '">Remove</button>';
        html += '</div></li>';
      });
      html += '</ul>';
      container.innerHTML = html;

      container.querySelectorAll('.wishlist-remove-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var itemId = btn.dataset.itemId;
          var variantId = btn.dataset.variantId;
          if (getSessionToken() && itemId) {
            apiRequest('DELETE', '/wishlist/items/' + itemId).then(function () {
              btn.closest('.wishlist-item').remove();
              if (!container.querySelector('.wishlist-item')) {
                container.innerHTML = '<p class="wishlist-empty">Your wishlist is empty.</p>';
              }
            });
          } else {
            var items = getGuestItems().filter(function (i) { return i.variant_id !== variantId; });
            saveGuestItems(items);
            btn.closest('.wishlist-item').remove();
            if (!container.querySelector('.wishlist-item')) {
              container.innerHTML = '<p class="wishlist-empty">Your wishlist is empty.</p>';
            }
          }
        });
      });
    }).catch(function () {
      container.innerHTML = '<p>Could not load wishlist. Please refresh.</p>';
    });
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // ─── Merge guest items after login ───────────────────────────────────────────

  function mergeGuestWishlist() {
    var items = getGuestItems();
    if (!items.length) return;

    apiRequest('POST', '/wishlist/merge', {
      store_domain: cfg.storeDomain,
      items: items.map(function (i) {
        return {
          product_id: i.product_id,
          variant_id: i.variant_id,
          product_handle: i.product_handle,
          product_title: i.product_title,
          image_url: i.image_url,
          price: i.price,
          currency: i.currency || 'NZD',
        };
      }),
    }).then(function () {
      localStorage.removeItem(GUEST_KEY);
    }).catch(function () { /* silent */ });
  }

  // ─── Init ────────────────────────────────────────────────────────────────────

  function init() {
    // If customer is logged in and we don't have a session token yet, authenticate
    if (cfg.customer && !getSessionToken()) {
      loginWithHmac(cfg.customer)
        .then(function () {
          mergeGuestWishlist();
          return loadWishlistedIds();
        })
        .catch(function () {
          loadWishlistedIds();
        });
    } else {
      loadWishlistedIds();
    }

    // Bind buttons already in DOM
    document.querySelectorAll('.wishlist-btn').forEach(function (btn) {
      btn.addEventListener('click', onButtonClick);
    });

    // Observe new buttons added dynamically (e.g. quick-view modals)
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          var btns = node.querySelectorAll ? node.querySelectorAll('.wishlist-btn') : [];
          btns.forEach(function (btn) {
            btn.addEventListener('click', onButtonClick);
            updateButton(btn);
          });
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Wishlist page
    var pageContainer = document.getElementById('wishlist-page-items');
    if (pageContainer) {
      renderWishlistPage(pageContainer);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose minimal public API for theme developers
  window.Wishlist = {
    reload: loadWishlistedIds,
    isWishlisted: function (variantId) { return wishlisted.has('gid://shopify/ProductVariant/' + variantId); },
    getGuestItems: getGuestItems,
  };
})();
